import React, {useContext, useMemo} from "react";
import {Box, Typography} from "@mui/material";
import {useParams} from "react-router-dom";
import {useQuery} from "react-query";

import AbcOutlinedIcon from "@mui/icons-material/AbcOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import CalculationTabs from "../../../components/CalculationTabs";
import {getABCVED} from "../../../api/abcvedApi";
import {LanguageContext} from "../../../components/LanguageContext";

const ABC_TONE = {A: "active", B: "info", C: "warning"};
const XYZ_TONE = {X: "active", Y: "info", Z: "warning"};
const VED_TONE = {V: "active", E: "info", D: "warning"};

const ClassBadge = ({value, toneMap}) => {
    if (!value) return <Typography color="text.disabled">—</Typography>;
    return (
        <StatusBadge
            status={toneMap[value.toUpperCase()] || "neutral"}
            showDot={false}
            label={value.toUpperCase()}
        />
    );
};

const AbcVedPage = () => {
    const {excelId} = useParams();
    const {translations} = useContext(LanguageContext) || {};

    const {data, isLoading} = useQuery({
        queryKey: ["getabcved", excelId],
        queryFn: () => getABCVED(excelId),
    });

    const columns = useMemo(
        () => [
            {
                field: "abcved_id",
                headerName: "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{fontFamily: "monospace"}}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "supplier_name",
                headerName:
                    translations?.poslovodjaProizvodiSupplierName || "Dobavljač",
                minWidth: 180,
                flex: 1,
            },
            {
                field: "code",
                headerName:
                    translations?.adminAbcVedPageProductCode || "Šifra proizvoda",
                minWidth: 140,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{fontFamily: "monospace", fontWeight: 600}}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminAbcVedPageProductName || "Naziv proizvoda",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "xyz",
                headerName: translations?.adminAbcVedPageXYZ || "XYZ",
                minWidth: 100,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <ClassBadge value={params.value} toneMap={XYZ_TONE}/>
                ),
            },
            {
                field: "abc",
                headerName: translations?.adminAbcVedPageABC || "ABC",
                minWidth: 100,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <ClassBadge value={params.value} toneMap={ABC_TONE}/>
                ),
            },
            {
                field: "ved",
                headerName: translations?.adminAbcVedPageVED || "VED",
                minWidth: 100,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <ClassBadge value={params.value} toneMap={VED_TONE}/>
                ),
            },
        ],
        [translations]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<AbcOutlinedIcon/>}
                eyebrow={translations?.adminExcelProInfoPageABCVed || "ABC-VED"}
                title={
                    translations?.adminAbcVedPageTitle || "ABC-VED klasifikacija"
                }
                description={
                    translations?.adminAbcVedPageSubtitle ||
                    "Klasifikacija proizvoda po značajnosti i varijabilnosti tražnje."
                }
            />

            <CalculationTabs active="abc-ved"/>

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    rowHeight={56}
                    emptyIcon={<AbcOutlinedIcon/>}
                    emptyTitle={
                        translations?.messageIfEmptyData ||
                        "Nema podataka za prikaz"
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default AbcVedPage;
